import {
  locationOpeningsListResponseSchema,
  locationUserDetailsResponseSchema,
  nextAvailabilityListResponseSchema,
  openingsListResponseSchema,
  scheduleOpeningsResponseSchema,
  toggleBreakResponseSchema,
  toggleExtraSlotResponseSchema,
  type LocationOpeningsListResponse,
  type LocationOpeningsMember,
  type LocationUserDetailsResponse,
  type NextAvailabilityItem,
  type NextAvailabilityListResponse,
  type OpeningsItem as ContractOpeningsItem,
  type OpeningsListResponse,
  type ScheduleOpeningsResponse,
  type ToggleBreakResponse,
  type ToggleExtraSlotResponse,
} from "@openings/api-contract";
import {
  BaseHttpClient,
  baseOk,
  ApiOk,
  DbDateString,
  DbTimeString,
} from "./base";

const toggleBreakResponse = baseOk(toggleBreakResponseSchema);
export type ToggleBreakResult = ApiOk<ToggleBreakResponse>;

const toggleExtraSlotResponse = baseOk(toggleExtraSlotResponseSchema);
export type ToggleExtraSlotResult = ApiOk<ToggleExtraSlotResponse>;

const openingsListResponse = baseOk(openingsListResponseSchema);
const nextOpeningsListResponse = baseOk(nextAvailabilityListResponseSchema);
const locationOpeningsListResponse = baseOk(locationOpeningsListResponseSchema);
const locationUserDetailsSchema = baseOk(locationUserDetailsResponseSchema);
const scheduleOpeningsResponse = baseOk(scheduleOpeningsResponseSchema);

export type OpeningsItem = ContractOpeningsItem;
export type NextOpeningsItem = NextAvailabilityItem;
export type LocationOpeningsItem = LocationOpeningsMember;
export type LocationUserDetails = LocationUserDetailsResponse;

// ---- Input Types ----
export interface GetOpeningsInput {
  salonId: string;
  username: string;
  date: DbDateString;
  duration: number; // minutes
}
export interface GetNextOpeningsInput {
  salonId: string;
  scheduleIds: string[];
  date: DbDateString;
  duration: number;
  daysAhead?: number;
  /**
   * Stop once this many dates have an opening (1–31). Without it the API
   * reads and returns the whole `daysAhead` window.
   */
  limit?: number;
  /**
   * Ids of the services being booked. The API then leaves out the starts
   * sooner than their largest minimum notice.
   */
  serviceIds?: string[];
}
export interface GetScheduleMembersInput {
  salonId: string;
  scheduleId: string;
  date: DbDateString;
  services: { serviceId: string; optionId?: string }[];
  /**
   * Return members on VACATION (with empty openings and `dayStatus:
   * "vacation"` + `vacationRange`) instead of omitting them, so the booking UI
   * can say "away until X" rather than silently hiding the person. A quiet
   * DAY_OFF resolves to `dayStatus: "offday"` and stays omitted either way —
   * that is the point of the two types.
   */
  includeVacation?: boolean;
}
/** @deprecated Use GetScheduleMembersInput */
export interface GetLocationOpeningsInput {
  salonId: string;
  locationId: string;
  date: DbDateString;
  services: { serviceId: string; optionId?: string }[];
}

// ---- Additional (Mini Profile) Types ----
export type GetOpeningsResult = ApiOk<OpeningsListResponse>;
export type GetNextOpeningsResult = ApiOk<NextAvailabilityListResponse>;
export type GetLocationOpeningsResult = ApiOk<LocationOpeningsListResponse>;
export type GetLocationUserDetailsResult = ApiOk<LocationUserDetails>;
// Single-schedule openings (API: GET /v1/openings?scheduleId=...)
export interface GetScheduleOpeningsInput {
  scheduleId: string;
  date: DbDateString;
  serviceDuration: number; // minutes
  /**
   * Ids of the services being booked. The API then leaves out the starts
   * sooner than their largest minimum notice.
   */
  serviceIds?: string[];
}
export type GetScheduleOpeningsResult = ApiOk<ScheduleOpeningsResponse>;

export class OpeningsClient extends BaseHttpClient {
  /**
   * @deprecated Use `getScheduleOpenings` for single-schedule availability or `getLocationOpenings` for multi-member availability.
   */
  getOpenings(input: GetOpeningsInput): Promise<GetOpeningsResult> {
    const qs = new URLSearchParams({
      salonId: input.salonId,
      username: input.username,
      date: input.date,
      duration: String(input.duration),
    });
    return this.get(`/v1/openings?${qs.toString()}`, openingsListResponse);
  }
  // Fetch openings for a single scheduleId
  getScheduleOpenings(
    input: GetScheduleOpeningsInput,
  ): Promise<GetScheduleOpeningsResult> {
    const qs = new URLSearchParams({
      scheduleId: input.scheduleId,
      date: input.date,
      serviceDuration: String(input.serviceDuration),
    });
    if (input.serviceIds?.length) {
      qs.set("serviceIds", input.serviceIds.join(","));
    }
    return this.get(`/v1/openings?${qs.toString()}`, scheduleOpeningsResponse);
  }
  getNextOpenings(input: GetNextOpeningsInput): Promise<GetNextOpeningsResult> {
    const qs = new URLSearchParams({
      salonId: input.salonId,
      scheduleIds: input.scheduleIds.join(","),
      date: input.date,
      serviceDuration: String(input.duration),
      ...(input.daysAhead ? { days: String(input.daysAhead) } : {}),
      ...(input.limit ? { limit: String(input.limit) } : {}),
    });
    if (input.serviceIds?.length) {
      qs.set("serviceIds", input.serviceIds.join(","));
    }
    return this.get(
      `/v1/openings/next-availability?${qs.toString()}`,
      nextOpeningsListResponse,
    );
  }
  toggleBreak(input: {
    scheduleId: string;
    date: DbDateString;
    time: DbTimeString;
  }): Promise<ToggleBreakResult> {
    return this.post(
      "/v1/breaks/toggle",
      {
        scheduleId: input.scheduleId,
        date: input.date,
        time: input.time,
      },
      toggleBreakResponse,
    );
  }
  toggleExtraSlot(input: {
    scheduleId: string;
    date: DbDateString;
    time: DbTimeString;
  }): Promise<ToggleExtraSlotResult> {
    return this.post(
      "/v1/extra-slots/toggle",
      {
        scheduleId: input.scheduleId,
        date: input.date,
        time: input.time,
      },
      toggleExtraSlotResponse,
    );
  }
  getLocationOpenings(
    input: GetLocationOpeningsInput | GetScheduleMembersInput,
  ): Promise<GetLocationOpeningsResult> {
    const scheduleId =
      "scheduleId" in input ? input.scheduleId : input.locationId;
    const qs = new URLSearchParams({
      salonId: input.salonId,
      scheduleId,
      date: input.date,
      services: JSON.stringify(input.services),
    });
    if ("includeVacation" in input && input.includeVacation) {
      qs.set("includeVacation", "true");
    }
    return this.get(
      `/v1/openings/members?${qs.toString()}`,
      locationOpeningsListResponse,
    );
  }
  /** New canonical method name */
  getScheduleMemberOpenings(
    input: GetScheduleMembersInput,
  ): Promise<GetLocationOpeningsResult> {
    return this.getLocationOpenings(input);
  }
  getLocationUserDetails(
    userId: string,
  ): Promise<GetLocationUserDetailsResult> {
    return this.get(`/v1/locations/users/${userId}`, locationUserDetailsSchema);
  }
}
